import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Wallet, Receipt, PiggyBank, ShieldCheck, Zap, ChevronLeft, ChevronRight,
  LogOut, TrendingUp, BarChart3, AlertTriangle, CheckCircle2, SlidersHorizontal, CalendarClock, X, Eye, ShoppingCart,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import WalletCard from "../components/WalletCard";
import BillsManager from "../components/managers/BillsManager";
import GoalManager from "../components/managers/GoalManager";
import UnplannedManager from "../components/managers/UnplannedManager";
import IncomeManager from "../components/managers/IncomeManager";
import HouseholdManager from "../components/managers/HouseholdManager";
import Reports from "../components/managers/Reports";
import SettingsDialog from "../components/managers/SettingsDialog";
import NotificationCenter from "../components/managers/NotificationCenter";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { money, monthLabel, shiftMonth, thisMonth, CURRENCIES } from "../lib/format";

const META = {
  bills: { key: "bills", testKey: "bills", title: "Bills Wallet", badge: "Fixed Expenses", icon: Receipt, color: "#EA580C", bg: "#FFEDD5", icon_color: "#C2410C" },
  household: { key: "household", testKey: "household", title: "Household Wallet", badge: "Groceries & Home", icon: ShoppingCart, color: "#65A30D", bg: "#ECFCCB", icon_color: "#4D7C0F" },
  savings: { key: "savings", testKey: "savings", title: "Savings Wallet", badge: "Goal Oriented", icon: PiggyBank, color: "#0D9488", bg: "#CCFBF1", icon_color: "#0F766E" },
  emergency: { key: "emergency", testKey: "emergency", title: "Emergency Fund", badge: "Safety Net", icon: ShieldCheck, color: "#0284C7", bg: "#E0F2FE", icon_color: "#0369A1" },
  unplanned: { key: "unplanned", testKey: "unplanned", title: "Unplanned Budget", badge: "Buffer Pool", icon: Zap, color: "#D97706", bg: "#FEF3C7", icon_color: "#B45309" },
  my_wallet: { key: "my_wallet", testKey: "my-wallet", title: "My Wallet", badge: "Safe to Spend", icon: Wallet, color: "#16A34A", bg: "#DCFCE7", icon_color: "#15803D" },
};

export default function Dashboard() {
  const { user, logout, setUser } = useAuth();
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null); // manager key
  const [notifKey, setNotifKey] = useState(0);
  const [reportMonth, setReportMonth] = useState(thisMonth());
  const [rolloverInfo, setRolloverInfo] = useState(null);
  const currency = data?.currency || user?.settings?.currency || "USD";
  const readOnly = user?.role !== "admin" && user?.status !== "approved";

  const load = useCallback(async () => {
    if (user?.status === "approved" || user?.role === "admin") {
      await api.post(`/rollover/${month}`).then(({ data: r }) => {
        if (!r.already_done) setRolloverInfo({ month, carried: r.carried, prev: r.prev_month });
      }).catch(() => {});
    }
    const { data } = await api.get(`/dashboard?month=${month}`);
    setData(data);
    setNotifKey((k) => k + 1);
  }, [month, user]);

  useEffect(() => { load(); }, [load]);

  const openReport = (m) => { setReportMonth(m); setActive("reports"); };

  const changeCurrency = async (code) => {
    if (readOnly) return toast.error("View-only account — editing is disabled.");
    const { data: u } = await api.put("/settings", { currency: code });
    setUser(u);
    toast.success(`Display currency set to ${code}`);
    load();
  };

  const walletOf = (key) => data?.wallets.find((w) => w.key === key) || {};

  const cap = data?.spending_cap || { cap: 0, spent: 0, pct: 0 };
  const capZone = cap.pct > 100 ? "danger" : cap.pct > 90 ? "orange" : cap.pct > 75 ? "amber" : "safe";
  const zoneStyle = {
    safe: { bar: "#10B981", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", icon: CheckCircle2, msg: "You're in the safe zone." },
    amber: { bar: "#F59E0B", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", icon: AlertTriangle, msg: "Approaching your spending cap." },
    orange: { bar: "#EA580C", bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", icon: AlertTriangle, msg: "Careful — nearing your soft cap." },
    danger: { bar: "#EF4444", bg: "bg-red-50", text: "text-red-700", border: "border-red-200", icon: AlertTriangle, msg: "Soft cap breached — spending over plan." },
  }[capZone];
  const ZoneIcon = zoneStyle.icon;

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Navbar */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between no-print">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-orange-600 flex items-center justify-center text-white"><Wallet className="w-5 h-5" /></div>
          <span className="font-display text-lg font-bold hidden sm:block">ePitaka</span>
        </div>
        <div className="flex items-center gap-2">
          <Select value={currency} onValueChange={changeCurrency}>
            <SelectTrigger data-testid="currency-switch" className="w-[110px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}</SelectContent>
          </Select>
          <NotificationCenter month={month} refreshKey={notifKey} />
          <Button variant="outline" size="sm" className="h-9" onClick={() => openReport(month)} data-testid="open-reports-button">
            <BarChart3 className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Reports</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-button" className="outline-none">
                <Avatar className="w-9 h-9 border border-slate-200"><AvatarFallback className="bg-orange-100 text-orange-700 text-sm font-semibold">{initials}</AvatarFallback></Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>
                <p className="text-sm font-semibold">{user?.name}</p>
                <p className="text-xs text-slate-400 font-normal">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setActive("settings")} data-testid="edit-budget-button"><SlidersHorizontal className="w-4 h-4 mr-2" /> Edit budget</DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-red-600"><LogOut className="w-4 h-4 mr-2" /> Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header row */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Financial Dashboard</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Hi {(user?.name || "").split(" ")[0]} 👋</h1>
          </div>
          <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="month-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <span data-testid="current-month" className="text-sm font-semibold text-slate-700 w-32 text-center">{monthLabel(month)}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="month-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>

        {/* Read-only preview banner */}
        {readOnly && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} data-testid="readonly-banner"
            className="mt-5 p-4 rounded-2xl border border-slate-300 bg-slate-900 text-white flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-orange-600/20 text-orange-400 flex items-center justify-center shrink-0">
              <Eye className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold">
                {user?.status === "demo" ? "Demo account — view only" : "Read-only preview"}
              </p>
              <p className="text-xs text-slate-300">
                {user?.status === "demo"
                  ? "This is a sample account for exploring ePitaka. Create your own account to start budgeting."
                  : "Your account is pending admin approval. You can browse everything, but editing is disabled for now."}
              </p>
            </div>
          </motion.div>
        )}

        {/* Rollover summary banner */}
        {rolloverInfo && rolloverInfo.month === month && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} data-testid="rollover-banner"
            className="mt-5 p-4 rounded-2xl border border-orange-200 bg-orange-50 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-orange-600/10 text-orange-700 flex items-center justify-center shrink-0">
              <CalendarClock className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-orange-800">Welcome to {monthLabel(month)}</p>
              <p className="text-xs text-orange-700/80">
                {rolloverInfo.carried > 0
                  ? `${rolloverInfo.carried} unpaid bill(s) carried forward from last month. `
                  : "No unpaid bills to carry over. "}
                Your previous month's summary is ready.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="h-8 border-orange-300 text-orange-700 hover:bg-orange-100"
                onClick={() => openReport(rolloverInfo.prev)} data-testid="rollover-view-report">
                View {monthLabel(rolloverInfo.prev)} report
              </Button>
              <button onClick={() => setRolloverInfo(null)} data-testid="rollover-dismiss" className="text-orange-400 hover:text-orange-700"><X className="w-4 h-4" /></button>
            </div>
          </motion.div>
        )}

        {/* Summary strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
          <StatCard label="Income (actual)" value={money(data?.income.actual || 0, currency)} sub={`of ${money(data?.income.target || 0, currency)}`} testid="stat-income" />
          <StatCard label="Free Money" value={money(data?.my_wallet.actual || 0, currency)} sub="My Wallet" danger={(data?.my_wallet.actual || 0) < 0} testid="stat-free" />
          <StatCard label="Savings Balance" value={money(data?.totals.savings_balance || 0, currency)} sub="all-time" testid="stat-savings" />
          <StatCard label="Emergency Balance" value={money(data?.totals.emergency_balance || 0, currency)} sub="all-time" testid="stat-emergency" />
        </div>

        {/* Expense limiter */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className={`mt-6 p-4 sm:p-5 rounded-2xl border ${zoneStyle.bg} ${zoneStyle.border}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ZoneIcon className={`w-4 h-4 ${zoneStyle.text}`} />
              <span className={`text-sm font-semibold ${zoneStyle.text}`}>Expense Limiter · {zoneStyle.msg}</span>
            </div>
            <span className={`text-sm font-mono-nums font-bold ${zoneStyle.text}`}>{cap.pct}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-white/60 overflow-hidden">
            <div data-testid="expense-limiter-progress" className="wallet-progress h-full rounded-full" style={{ width: `${Math.min(cap.pct, 100)}%`, background: zoneStyle.bar }} />
          </div>
          <div className="flex justify-between mt-2 text-xs text-slate-500 font-mono-nums">
            <span>Spent {money(cap.spent, currency)}</span>
            <span>Cap {money(cap.cap, currency)}</span>
          </div>
        </motion.div>

        {/* Wallet grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          <WalletCard meta={META.bills} data={walletOf("bills")} currency={currency} index={0} onManage={() => setActive("bills")} />
          <WalletCard meta={META.household} data={walletOf("household")} currency={currency} index={1} onManage={() => setActive("household")} />
          <WalletCard meta={META.savings} data={walletOf("savings")} currency={currency} index={2} onManage={() => setActive("savings")} />
          <WalletCard meta={META.emergency} data={walletOf("emergency")} currency={currency} index={3} onManage={() => setActive("emergency")} />
          <WalletCard meta={META.unplanned} data={walletOf("unplanned")} currency={currency} index={4} onManage={() => setActive("unplanned")} />
          <WalletCard meta={META.my_wallet} data={data?.my_wallet} currency={currency} index={5} onManage={() => setActive("income")} />

          {/* Income tracker card */}
          <motion.button
            data-testid="income-tracker-card"
            onClick={() => setActive("income")}
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            whileHover={{ y: -3 }}
            className="text-left bg-[#0F172A] text-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-orange-600/20 flex items-center justify-center text-orange-400"><TrendingUp className="w-5 h-5" /></div>
              <div>
                <p className="font-display font-semibold text-[15px]">Income Tracker</p>
                <span className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Expected vs Actual</span>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wider text-slate-400">Received this month</p>
              <p className="font-mono-nums text-2xl font-bold">{money(data?.income.actual || 0, currency)}</p>
              <p className="text-xs text-slate-400 font-mono-nums">of {money(data?.income.expected || 0, currency)} expected</p>
            </div>
            <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="wallet-progress h-full rounded-full bg-orange-500" style={{ width: `${Math.min(data?.income.pct || 0, 100)}%` }} />
            </div>
          </motion.button>
        </div>
      </main>

      {/* Managers */}
      <BillsManager open={active === "bills"} onOpenChange={(o) => !o && setActive(null)} month={month} currency={currency} onChanged={load} readOnly={readOnly} />
      <HouseholdManager open={active === "household"} onOpenChange={(o) => !o && setActive(null)} month={month} currency={currency} onChanged={load} readOnly={readOnly} />
      <GoalManager kind="savings" open={active === "savings"} onOpenChange={(o) => !o && setActive(null)} month={month} currency={currency} onChanged={load} readOnly={readOnly} />
      <GoalManager kind="emergency" open={active === "emergency"} onOpenChange={(o) => !o && setActive(null)} month={month} currency={currency} onChanged={load} readOnly={readOnly} />
      <UnplannedManager open={active === "unplanned"} onOpenChange={(o) => !o && setActive(null)} month={month} currency={currency} onChanged={load} readOnly={readOnly} />
      <IncomeManager open={active === "income"} onOpenChange={(o) => !o && setActive(null)} month={month} currency={currency} onChanged={load} readOnly={readOnly} />
      <Reports open={active === "reports"} onOpenChange={(o) => !o && setActive(null)} month={reportMonth} currency={currency} />
      <SettingsDialog open={active === "settings"} onOpenChange={(o) => !o && setActive(null)} user={user} onSaved={(u) => { setUser(u); load(); }} readOnly={readOnly} />
    </div>
  );
}

function StatCard({ label, value, sub, danger, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-4">
      <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`font-mono-nums text-xl font-bold mt-0.5 ${danger ? "text-red-600" : "text-slate-900"}`}>{value}</p>
      <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
    </div>
  );
}
